import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import api from '../../utils/api';
import {
  HandCoins, Loader2, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Download, AlertTriangle, Users, Building, Euro,
  Banknote, CheckCircle2, Send, FileWarning
} from 'lucide-react';
import { toast } from 'sonner';
import { handleNameCorrections } from '../../utils/nameCorrections';

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

const COLUMNS = [
  { key: 'property_code', label: 'Codice' },
  { key: 'nome_struttura', label: 'Immobile' },
  { key: 'segnalatore_nome', label: 'Segnalatore' },
  { key: 'proprietario', label: 'Proprietario' },
  { key: 'citta', label: 'Città' },
  { key: 'stato', label: 'Stato' },
  { key: 'num_prenotazioni', label: 'Pren.', numeric: true },
  { key: 'importo_pagato_proprietario', label: 'Importo Proprietario', numeric: true },
  { key: 'percentuale_partner', label: '% Partner', numeric: true },
  { key: 'compenso', label: 'Compenso', numeric: true },
];

export default function SegnalatoriCompensiPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [segFilter, setSegFilter] = useState('all');
  const [statoFilter, setStatoFilter] = useState('all');
  const [sortCol, setSortCol] = useState('compenso');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedFatture, setSelectedFatture] = useState(() => new Set());
  const [generating, setGenerating] = useState(false);
  const [execDate, setExecDate] = useState('');
  const [batches, setBatches] = useState([]);
  const [uploadingBatch, setUploadingBatch] = useState(null);

  const loadBatches = async (year) => {
    try {
      const res = await api.get(`/segnalatori/batches?anno=${year}`);
      setBatches(res.data?.batches || []);
    } catch {
      setBatches([]);
    }
  };

  const downloadBatchFile = async (batchId, kind, fallbackName) => {
    try {
      const res = await api.get(`/segnalatori/batches/${batchId}/download?kind=${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = fallbackName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore download file');
    }
  };

  const handleUploadContabile = async (batchId, file) => {
    if (!file) return;
    setUploadingBatch(batchId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/segnalatori/batches/${batchId}/upload-response`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      const es = d.email_stats || {};
      let msg = `Contabile caricata: ${d.fatture_pagate || 0} fatture pagate`;
      if (es.sent) msg += ` · ${es.sent} email di conferma inviate ai segnalatori`;
      if (es.no_email) msg += ` · ${es.no_email} senza email`;
      toast.success(msg, { duration: 8000 });
      await handleNameCorrections(d.name_corrections);
      await loadBatches(anno);
      fetchData(anno);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento contabile');
    } finally {
      setUploadingBatch(null);
    }
  };

  const fetchData = async (year) => {
    setLoading(true);
    try {
      const res = await api.get(`/segnalatori/compensi?anno=${year}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore caricamento compensi');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(anno); loadBatches(anno); /* eslint-disable-next-line */ }, [anno]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40 inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 inline ml-1 text-amber-600" />
      : <ArrowDown className="w-3.5 h-3.5 inline ml-1 text-amber-600" />;
  };

  const segnalatoriOptions = useMemo(() => {
    const s = data?.per_segnalatore || [];
    return s.map((x) => ({ value: x.segnalatore_id, label: x.nome }));
  }, [data]);

  const filteredRows = useMemo(() => {
    let rows = [...(data?.rows || [])];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.property_code, r.nome_struttura, r.segnalatore_nome, r.proprietario, r.citta]
          .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
      );
    }
    if (segFilter !== 'all') rows = rows.filter((r) => r.segnalatore_id === segFilter);
    if (statoFilter !== 'all') rows = rows.filter((r) => r.stato === statoFilter);

    rows.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'number' || typeof vb === 'number') {
        va = Number(va || 0); vb = Number(vb || 0);
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      va = String(va || '').toLowerCase(); vb = String(vb || '').toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return rows;
  }, [data, search, segFilter, statoFilter, sortCol, sortDir]);

  const totals = useMemo(() => {
    return filteredRows.reduce((acc, r) => {
      acc.compenso += Number(r.compenso || 0);
      acc.importo += Number(r.importo_pagato_proprietario || 0);
      acc.pren += Number(r.num_prenotazioni || 0);
      return acc;
    }, { compenso: 0, importo: 0, pren: 0 });
  }, [filteredRows]);

  const fatture = data?.fatture || [];
  const fattureDaPagare = useMemo(() => fatture.filter((f) => f.stato_pagamento !== 'pagato'), [fatture]);
  const selectableIds = useMemo(() => fattureDaPagare.filter((f) => f.has_iban).map((f) => f.id), [fattureDaPagare]);
  const selectedTotal = useMemo(
    () => fatture.filter((f) => selectedFatture.has(f.id)).reduce((s, f) => s + Number(f.importo || 0), 0),
    [fatture, selectedFatture]
  );

  const toggleFattura = (id) => setSelectedFatture((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleSelectAll = () => setSelectedFatture((prev) =>
    (prev.size === selectableIds.length && selectableIds.length > 0) ? new Set() : new Set(selectableIds)
  );

  const handleGeneraSepa = async () => {
    const ids = [...selectedFatture];
    if (!ids.length) { toast.error('Seleziona almeno una fattura da liquidare'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/segnalatori/compensi/genera-sepa', {
        fattura_ids: ids,
        execution_date: execDate || undefined,
      });
      const d = res.data;
      // Download del file XML CBI
      const bytes = Uint8Array.from(atob(d.cbi_xml_b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.cbi_filename; a.click();
      URL.revokeObjectURL(url);
      let msg = `Distinta SEPA generata: ${d.count} bonifici · ${fmtEur(d.total_amount)}`;
      if (d.hwa_notified) msg += ` · ${d.hwa_notified} segnalatori notificati a HW Academy`;
      toast.success(msg);
      if (d.skipped?.length) toast.warning(`${d.skipped.length} fattura/e escluse dall’XML SEPA: coordinate non valide o bonifico extra-SEPA da gestire separatamente`, { duration: 9000 });
      setSelectedFatture(new Set());
      fetchData(anno);
      loadBatches(anno);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione distinta SEPA');
    } finally {
      setGenerating(false);
    }
  };

  const exportCsv = () => {
    const header = ['Codice', 'Immobile', 'Segnalatore', 'Email Segnalatore', 'Proprietario', 'Città', 'Provincia', 'Stato', 'Prenotazioni', 'Importo Proprietario', '% Partner', 'Compenso'];
    const lines = filteredRows.map((r) => [
      r.property_code, r.nome_struttura, r.segnalatore_nome, r.segnalatore_email, r.proprietario,
      r.citta, r.provincia, r.stato, r.num_prenotazioni,
      String(r.importo_pagato_proprietario).replace('.', ','),
      r.percentuale_partner,
      String(r.compenso).replace('.', ','),
    ].map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'));
    const csv = [header.join(';'), ...lines].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compensi_segnalatori_${anno}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const annoOptions = [];
  const cy = new Date().getFullYear();
  for (let y = cy + 1; y >= cy - 4; y--) annoOptions.push(y);

  return (
    <Layout title="Compensi Segnalatori">
      <div className="space-y-6" data-testid="compensi-segnalatori-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
              <HandCoins className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Compensi Segnalatori</h1>
              <p className="text-sm text-slate-500">Rete Segnalatori · HW Academy — anno {anno}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
              <SelectTrigger className="w-28" data-testid="anno-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {annoOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => fetchData(anno)} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!filteredRows.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Users className="w-4 h-4" /> Segnalatori</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="kpi-segnalatori">{data?.per_segnalatore?.length || 0}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Building className="w-4 h-4" /> Immobili</div>
            <div className="text-2xl font-bold text-slate-900" data-testid="kpi-immobili">{filteredRows.length}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Euro className="w-4 h-4" /> Importo Proprietari</div>
            <div className="text-2xl font-bold text-slate-900">{fmtEur(totals.importo)}</div>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-2 text-amber-700 text-xs mb-1"><HandCoins className="w-4 h-4" /> Compensi Totali</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="kpi-compensi">{fmtEur(totals.compenso)}</div>
          </Card>
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-700 text-xs mb-1"><Banknote className="w-4 h-4" /> Da Liquidare</div>
            <div className="text-2xl font-bold text-emerald-700" data-testid="kpi-da-liquidare">{fmtEur(data?.totale_da_liquidare || 0)}</div>
          </Card>
        </div>

        {/* Per-segnalatore progress toward €5.000 */}
        {(data?.per_segnalatore?.length > 0) && (
          <Card className="p-4">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Avanzamento verso il limite annuo di {fmtEur(data.limite_compenso)} per segnalatore</h2>
            <div className="space-y-3">
              {data.per_segnalatore.map((s) => (
                <div key={s.segnalatore_id} data-testid={`seg-progress-${s.segnalatore_id}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-800">
                      {s.nome} <span className="text-slate-400 font-normal">· {s.num_immobili} immobili · {s.num_prenotazioni} pren.</span>
                    </span>
                    <span className={`font-semibold ${s.supera_limite ? 'text-red-600' : 'text-slate-700'}`}>
                      {fmtEur(s.compenso_totale)} <span className="text-xs text-slate-400">/ {fmtEur(data.limite_compenso)}</span>
                      {s.compenso_da_liquidare > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-700 font-medium" data-testid={`seg-da-liquidare-${s.segnalatore_id}`}>
                          <Banknote className="w-3 h-3" /> da liquidare {fmtEur(s.compenso_da_liquidare)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.supera_limite ? 'bg-red-500' : s.percentuale_limite > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(s.percentuale_limite, 100)}%` }}
                    />
                  </div>
                  {s.supera_limite && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Limite €5.000 superato
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Fatture da liquidare (SEPA) */}
        <Card className="p-4" data-testid="sepa-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600" />
              <h2 className="text-sm font-semibold text-slate-700">
                Fatture da liquidare · <span className="text-emerald-700">{fmtEur(data?.totale_da_liquidare || 0)}</span>
                <span className="text-slate-400 font-normal"> ({fattureDaPagare.length})</span>
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-slate-500">Data esecuzione</label>
              <Input
                type="date"
                value={execDate}
                onChange={(e) => setExecDate(e.target.value)}
                className="w-40 h-9"
                data-testid="sepa-exec-date"
              />
              <Button
                onClick={handleGeneraSepa}
                disabled={generating || selectedFatture.size === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="genera-sepa-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Genera distinta SEPA ({selectedFatture.size})
              </Button>
            </div>
          </div>
          {fattureDaPagare.length === 0 ? (
            <div className="text-center text-slate-400 py-6 text-sm" data-testid="sepa-empty">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Nessuna fattura in attesa di liquidazione.<br />
              Le fatture arrivano automaticamente da HW Academy quando i segnalatori le caricano.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fatture-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    <th className="px-3 py-2 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={selectedFatture.size > 0 && selectedFatture.size === selectableIds.length}
                        onChange={toggleSelectAll}
                        data-testid="select-all-fatture"
                      />
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">Segnalatore</th>
                    <th className="px-3 py-2 text-left font-semibold">Periodo</th>
                    <th className="px-3 py-2 text-left font-semibold">Tipo</th>
                    <th className="px-3 py-2 text-left font-semibold">IBAN</th>
                    <th className="px-3 py-2 text-right font-semibold">Importo</th>
                  </tr>
                </thead>
                <tbody>
                  {fattureDaPagare.map((f) => (
                    <tr
                      key={f.id}
                      className={`border-b border-slate-100 ${selectedFatture.has(f.id) ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`}
                      data-testid={`fattura-${f.id}`}
                    >
                      <td className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          disabled={!f.has_iban}
                          checked={selectedFatture.has(f.id)}
                          onChange={() => toggleFattura(f.id)}
                          data-testid={`select-fattura-${f.id}`}
                        />
                      </td>
                      <td className="px-3 py-2.5 font-medium text-slate-800">{f.segnalatore_nome}</td>
                      <td className="px-3 py-2.5 text-slate-600 tabular-nums">{String(f.mese).padStart(2, '0')}/{f.anno}</td>
                      <td className="px-3 py-2.5 text-slate-500 capitalize">{f.tipo_documento || '—'}</td>
                      <td className="px-3 py-2.5">
                        {f.has_iban ? (
                          <span className="font-mono text-xs text-slate-600">{f.iban}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600">
                            <FileWarning className="w-3.5 h-3.5" /> IBAN mancante
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-800">{fmtEur(f.importo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-800">
                    <td className="px-3 py-2.5"></td>
                    <td className="px-3 py-2.5" colSpan={3}>Selezionate: {selectedFatture.size}</td>
                    <td className="px-3 py-2.5 text-right text-slate-500 text-xs">Totale selezionato</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{fmtEur(selectedTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        {/* Distinte SEPA generate: download XML, upload contabile banca */}
        <Card className="p-4" data-testid="seg-batches-section">
          <div className="flex items-center gap-2 mb-3">
            <Download className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-semibold text-slate-700">
              Distinte SEPA generate <span className="text-slate-400 font-normal">({batches.length})</span>
            </h2>
          </div>
          {batches.length === 0 ? (
            <div className="text-center text-slate-400 py-4 text-sm" data-testid="seg-batches-empty">
              Nessuna distinta generata per il {anno}.
            </div>
          ) : (
            <div className="space-y-2">
              {batches.map((b) => (
                <div key={b.id} className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg px-3 py-2.5" data-testid={`seg-batch-${b.id}`}>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {b.status === 'paid' ? 'Pagato' : 'Generato'}
                  </span>
                  <span className="text-sm text-slate-600">{new Date(b.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <span className="text-sm text-slate-700">{b.total_transactions || (b.transactions || []).length || '-'} bonifici</span>
                  <span className="text-sm font-semibold text-slate-800 tabular-nums">{fmtEur(b.total_amount)}</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <Button size="sm" variant="outline" onClick={() => downloadBatchFile(b.id, 'cbi', b.cbi_filename || 'distinta.xml')} data-testid={`seg-batch-xml-${b.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> XML
                    </Button>
                    {b.bank_response_filename ? (
                      <Button size="sm" variant="outline" onClick={() => downloadBatchFile(b.id, 'bank_response', b.bank_response_filename)} data-testid={`seg-batch-receipt-${b.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Contabile
                      </Button>
                    ) : (
                      <label className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border bg-white hover:bg-slate-50 cursor-pointer"
                             title="Carica il PDF contabile: marca pagato, corregge i nomi e invia le email di conferma ai segnalatori"
                             data-testid={`seg-batch-upload-${b.id}`}>
                        {uploadingBatch === b.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                        Carica contabile
                        <input type="file" accept=".pdf" className="hidden" disabled={uploadingBatch === b.id}
                               onChange={(e) => { handleUploadContabile(b.id, e.target.files?.[0]); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cerca immobile, segnalatore, proprietario, città..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={segFilter} onValueChange={setSegFilter}>
              <SelectTrigger className="w-56" data-testid="segnalatore-filter"><SelectValue placeholder="Tutti i segnalatori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i segnalatori</SelectItem>
                {segnalatoriOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger className="w-40" data-testid="stato-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento...
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="empty-state">
              <HandCoins className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Nessun immobile con segnalatore per i filtri selezionati.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compensi-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => toggleSort(c.key)}
                        className={`px-3 py-3 font-semibold cursor-pointer select-none hover:bg-slate-100 whitespace-nowrap ${c.numeric ? 'text-right' : 'text-left'}`}
                        data-testid={`th-${c.key}`}
                      >
                        {c.label}<SortIcon col={c.key} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.property_id} className="border-b border-slate-100 hover:bg-amber-50/40" data-testid={`row-${r.property_id}`}>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.property_code || '—'}</td>
                      <td className="px-3 py-2.5 font-medium text-slate-800">{r.nome_struttura}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-slate-800">{r.segnalatore_nome}</div>
                        {r.segnalatore_email && <div className="text-xs text-slate-400">{r.segnalatore_email}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{r.proprietario || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{r.citta || '—'}{r.provincia ? ` (${r.provincia})` : ''}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${r.stato === 'online' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                          {r.stato}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.num_prenotazioni}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmtEur(r.importo_pagato_proprietario)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{r.percentuale_partner}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-amber-700">{fmtEur(r.compenso)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-800">
                    <td className="px-3 py-3" colSpan={6}>Totale ({filteredRows.length})</td>
                    <td className="px-3 py-3 text-right tabular-nums">{totals.pren}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{fmtEur(totals.importo)}</td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3 text-right tabular-nums text-amber-700">{fmtEur(totals.compenso)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
